import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Users,
  UserPlus,
  Search,
  ShieldAlert,
  ShieldCheck,
  MoreVertical,
  Edit2,
  Ban,
  Trash2,
  Mail,
  Phone,
  Calendar,
  Key,
  UserCheck,
  Shield,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Layers,
  BookOpen,
  CheckCircle2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/common/Button";
import { AdminUserData, AdminUserKpis } from "../../services/userService";
import {
  useAdminUsersQuery,
  useToggleUserStatusMutation,
  useDeleteUserMutation,
} from "../../hooks/queries/useUserAdminQueries";
import { useToast } from "../../hooks/otherHooks/useToast";
import RoleAssignmentModal from "./components/RoleAssignmentModal";
import UserPermissionModal from "./components/UserPermissionModal";
import ProvisionUserModal from "./components/ProvisionUserModal";

const GlassCard = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}>
    {children}
  </div>
);

const SuperAdminUsers: React.FC = () => {
  const { addToast } = useToast();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterRole, setFilterRole] = useState("All Roles");
  const [filterStatus, setFilterStatus] = useState("all");
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);

  // Modals state
  const [roleModalUser, setRoleModalUser] = useState<AdminUserData | null>(null);
  const [permissionModalUser, setPermissionModalUser] = useState<AdminUserData | null>(null);
  const [isProvisionOpen, setIsProvisionOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState<AdminUserData | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Live search debounce (matches staffJobs pattern)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (deletingUser) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [deletingUser]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setDebouncedSearch(searchQuery);
    }
  };

  // Structured query params
  const queryParams = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      role: filterRole !== "All Roles" ? filterRole : undefined,
      status: filterStatus !== "all" ? filterStatus : undefined,
    }),
    [debouncedSearch, filterRole, filterStatus]
  );

  // TanStack Query for User Management Directory & KPIs (cached with cost-saving staleTime)
  const {
    data: usersData,
    isLoading: isUsersLoading,
    isFetching: isUsersFetching,
    refetch: refetchUsers,
  } = useAdminUsersQuery(queryParams);

  const users = useMemo<AdminUserData[]>(
    () => (Array.isArray(usersData?.users) ? usersData.users : []),
    [usersData?.users]
  );

  const kpis = useMemo<AdminUserKpis>(
    () =>
      usersData?.kpis || {
        total: 0,
        superadmins: 0,
        instructors: 0,
        admins: 0,
        students: 0,
        suspended: 0,
      },
    [usersData?.kpis]
  );

  const loading = isUsersLoading && !usersData;

  // Mutations
  const { mutateAsync: toggleStatusMutation } = useToggleUserStatusMutation();
  const { mutateAsync: deleteUserMutation, isPending: isDeleting } = useDeleteUserMutation();

  // Handle outside click to close dropdown menu without a blocking overlay
  useEffect(() => {
    const handlePointerDownOutside = (event: MouseEvent | TouchEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };
    if (activeDropdown !== null) {
      document.addEventListener("mousedown", handlePointerDownOutside);
      document.addEventListener("touchstart", handlePointerDownOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handlePointerDownOutside);
      document.removeEventListener("touchstart", handlePointerDownOutside);
    };
  }, [activeDropdown]);

  // Status toggle handler
  const handleToggleStatus = async (user: AdminUserData) => {
    const newStatus = !user.is_active;
    try {
      await toggleStatusMutation({ id: user.id, isActive: newStatus });
      addToast(`Account for ${user.email} marked as ${newStatus ? "Active" : "Suspended"}.`, "success");
    } catch (err: any) {
      console.error("Failed to update status:", err);
      addToast("Failed to update account status.", "error");
    }
  };

  // Delete user handler
  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    try {
      await deleteUserMutation(deletingUser.id);
      addToast(`User account ${deletingUser.email} permanently removed.`, "success");
      setDeletingUser(null);
    } catch (err: any) {
      console.error("Failed to delete user:", err);
      addToast(err?.response?.data?.detail || "Failed to remove user account.", "error");
    }
  };

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700">
      {/* Header */}
      <header className="cht-flex cht-flex-col md:cht-flex-row cht-items-start md:cht-items-center cht-justify-between cht-gap-4">
        <div>
          <div className="cht-flex cht-items-center cht-gap-2.5 cht-mb-1">
            <span className="cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black cht-uppercase cht-tracking-wider cht-bg-[#3e57da]/10 cht-text-[#3e57da]">
              Security & Access Control
            </span>
            <span className="cht-text-xs cht-text-gray-400 font-medium">Role-Based Governance (RBAC)</span>
          </div>
          <h1 className="cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight !cht-font-heading">
            SuperAdmin User Directory
          </h1>
          <p className="cht-text-gray-500 cht-font-medium cht-text-sm">
            Master multi-role assignments, faculty provisioning, permissions, and account status controls.
          </p>
        </div>
        <div className="cht-flex cht-items-center cht-gap-3">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<RefreshCw size={16} className={isUsersFetching ? "cht-animate-spin" : ""} />}
            className="cht-rounded-2xl cht-border cht-border-gray-200"
            onClick={() => refetchUsers()}
            disabled={isUsersFetching}
          >
            Refresh
          </Button>
          <Button
            variant="primary"
            leftIcon={<UserPlus size={18} />}
            className="cht-rounded-2xl cht-px-6 cht-shadow-md hover:cht-shadow-lg"
            onClick={() => setIsProvisionOpen(true)}
          >
            Provision User Account
          </Button>
        </div>
      </header>

      {/* KPI Grid */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-5 cht-gap-5">
        <GlassCard className="cht-p-5 cht-rounded-[24px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-3.5 cht-bg-blue-50 cht-text-[#3e57da] cht-rounded-2xl">
            <Users size={22} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Total Accounts
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-slate-900">{kpis.total}</h4>
          </div>
        </GlassCard>

        <GlassCard className="cht-p-5 cht-rounded-[24px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-3.5 cht-bg-purple-50 cht-text-purple-600 cht-rounded-2xl">
            <ShieldAlert size={22} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              SuperAdmins
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-slate-900">{kpis.superadmins}</h4>
          </div>
        </GlassCard>

        <GlassCard className="cht-p-5 cht-rounded-[24px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-3.5 cht-bg-indigo-50 cht-text-indigo-600 cht-rounded-2xl">
            <BookOpen size={22} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Faculty / Instructors
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-slate-900">{kpis.instructors}</h4>
          </div>
        </GlassCard>

        <GlassCard className="cht-p-5 cht-rounded-[24px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-3.5 cht-bg-slate-100 cht-text-slate-700 cht-rounded-2xl">
            <ShieldCheck size={22} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              System Admins
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-slate-900">{kpis.admins}</h4>
          </div>
        </GlassCard>

        <GlassCard className="cht-p-5 cht-rounded-[24px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-3.5 cht-bg-rose-50 cht-text-rose-600 cht-rounded-2xl">
            <Ban size={22} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Suspended
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-slate-900">{kpis.suspended}</h4>
          </div>
        </GlassCard>
      </div>

      {/* Filter Bar */}
      <div className="cht-flex cht-flex-col lg:cht-flex-row cht-items-center cht-gap-4 cht-bg-white cht-p-4 cht-rounded-[24px] cht-border cht-border-gray-100 cht-shadow-sm">
        <div className="cht-relative cht-flex-1 cht-w-full">
          <Search className="cht-absolute cht-left-4 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by name, email, or phone number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-xl cht-py-3 cht-pl-12 cht-pr-4 cht-text-sm cht-font-bold text-slate-800 focus:cht-ring-2 focus:cht-ring-[#3e57da]/20 cht-transition-all"
          />
        </div>

        <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2 cht-w-full lg:cht-w-auto">
          {/* Role Filter Chips */}
          <div className="cht-flex cht-items-center cht-gap-1 cht-bg-gray-50 cht-p-1 cht-rounded-xl">
            {["All Roles", "STUDENT", "INSTRUCTOR", "ADMIN", "SUPERADMIN"].map((r) => (
              <button
                key={r}
                onClick={() => setFilterRole(r)}
                className={`cht-px-3 cht-py-1.5 cht-rounded-lg cht-text-[11px] cht-font-bold cht-transition-all ${filterRole === r
                    ? "cht-bg-[#1e293b] cht-text-white cht-shadow-xs"
                    : "cht-text-gray-500 hover:cht-text-gray-900"
                  }`}
              >
                {r === "All Roles" ? "All Roles" : r === "INSTRUCTOR" ? "Faculty" : r}
              </button>
            ))}
          </div>

          {/* Status Filter */}
          <div className="cht-flex cht-items-center cht-gap-1 cht-bg-gray-50 cht-p-1 cht-rounded-xl">
            {[
              { id: "all", label: "All Status" },
              { id: "active", label: "Active" },
              { id: "suspended", label: "Suspended" },
            ].map((s) => (
              <button
                key={s.id}
                onClick={() => setFilterStatus(s.id)}
                className={`cht-px-3 cht-py-1.5 cht-rounded-lg cht-text-[11px] cht-font-bold cht-transition-all ${filterStatus === s.id
                    ? "cht-bg-[#3e57da] cht-text-white cht-shadow-xs"
                    : "cht-text-gray-500 hover:cht-text-gray-900"
                  }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Users Directory Table */}
      <GlassCard className="cht-rounded-[32px] cht-overflow-hidden">
        {loading ? (
          <div className="cht-py-20 cht-flex cht-flex-col cht-items-center cht-justify-center cht-gap-3">
            <Loader2 size={32} className="cht-animate-spin cht-text-[#3e57da]" />
            <p className="cht-text-xs cht-font-bold cht-text-slate-500">Querying platform user records...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="cht-py-20 cht-text-center cht-px-4">
            <Users size={48} className="cht-text-slate-300 cht-mx-auto cht-mb-3" />
            <h4 className="cht-text-lg cht-font-bold cht-text-slate-800 !cht-font-heading">
              No user accounts found
            </h4>
            <p className="cht-text-xs cht-text-slate-500 cht-max-w-md cht-mx-auto cht-mt-1">
              {searchQuery
                ? `No accounts matching "${searchQuery}". Try adjusting your filters.`
                : "Your platform user directory is ready for its first accounts."}
            </p>
            <Button
              variant="primary"
              size="sm"
              leftIcon={<UserPlus size={16} />}
              className="cht-mt-5 cht-rounded-xl"
              onClick={() => setIsProvisionOpen(true)}
            >
              Provision Account Now
            </Button>
          </div>
        ) : (
          <div className="cht-overflow-x-auto cht-min-h-[380px] cht-pb-28">
            <table className="cht-w-full">
              <thead>
                <tr className="cht-border-b cht-border-gray-100 cht-bg-slate-50/50">
                  <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    User Identity
                  </th>
                  <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Primary Role
                  </th>
                  <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Multi-Role Authorizations
                  </th>
                  <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Account Status
                  </th>
                  <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Date Onboarded
                  </th>
                  <th className="cht-px-6 cht-py-4 cht-text-right cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    SuperAdmin Actions
                  </th>
                </tr>
              </thead>
              <tbody className="cht-divide-y cht-divide-gray-50">
                {users.map((user, index) => {
                  const isMenuOpen = activeDropdown === user.id;
                  const isNearBottom = index >= users.length - 2 && users.length > 2;
                  const secondaryRoles = (user.roles || []).filter((r) => r !== user.role);

                  return (
                    <tr
                      key={user.id}
                      className={`hover:cht-bg-indigo-50/30 cht-transition-colors group ${isMenuOpen ? "cht-relative cht-z-30" : ""
                        }`}
                    >
                      {/* User Identity */}
                      <td className="cht-px-6 cht-py-4">
                        <div className="cht-flex cht-items-center cht-gap-3.5">
                          <div className="cht-w-10 cht-h-10 cht-rounded-xl cht-bg-slate-100 cht-overflow-hidden cht-relative cht-shrink-0 cht-border cht-border-gray-200">
                            {user.photo_url ? (
                              <img
                                src={user.photo_url}
                                alt={user.name}
                                className="cht-w-full cht-h-full cht-object-cover"
                              />
                            ) : (
                              <div className="cht-w-full cht-h-full cht-flex cht-items-center cht-justify-center cht-text-[#3e57da] cht-font-black cht-text-sm">
                                {user.first_name ? user.first_name.charAt(0) : "U"}
                              </div>
                            )}
                          </div>
                          <div className="cht-min-w-0">
                            <h4 className="cht-text-sm cht-font-bold cht-text-slate-900 cht-truncate flex cht-items-center cht-gap-1.5">
                              {user.name}
                              {user.is_superuser && (
                                <span title="SuperUser Root" className="cht-inline-flex cht-items-center">
                                  <ShieldAlert size={14} className="cht-text-purple-600" />
                                </span>
                              )}
                            </h4>
                            <div className="cht-flex cht-items-center cht-gap-3 cht-mt-0.5 cht-text-[11px] cht-text-slate-400">
                              <span className="cht-truncate flex cht-items-center cht-gap-1">
                                <Mail size={11} /> {user.email}
                              </span>
                              {user.phone_number && (
                                <span className="cht-truncate flex cht-items-center cht-gap-1">
                                  <Phone size={11} /> {user.phone_number}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Primary Role Badge */}
                      <td className="cht-px-6 cht-py-4">
                        <span
                          className={`cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-1 cht-rounded-full cht-text-[10px] cht-font-black cht-uppercase cht-tracking-wider ${user.role === "SUPERADMIN"
                              ? "cht-bg-purple-100 cht-text-purple-800 cht-border cht-border-purple-200"
                              : user.role === "ADMIN"
                                ? "cht-bg-slate-100 cht-text-slate-800 cht-border cht-border-slate-200"
                                : user.role === "INSTRUCTOR"
                                  ? "cht-bg-blue-50 cht-text-[#3e57da] cht-border cht-border-blue-200"
                                  : "cht-bg-emerald-50 cht-text-emerald-700 cht-border cht-border-emerald-200"
                            }`}
                        >
                          {user.role}
                        </span>
                      </td>

                      {/* Authorized Roles List */}
                      <td className="cht-px-6 cht-py-4">
                        <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-1.5">
                          {(user.roles || [user.role]).map((r) => (
                            <span
                              key={r}
                              className={`cht-text-[9px] cht-font-black cht-uppercase cht-px-2 cht-py-0.5 cht-rounded-md ${r === user.role
                                  ? "cht-bg-slate-800 cht-text-white"
                                  : "cht-bg-slate-100 cht-text-slate-600"
                                }`}
                              title={r === user.role ? "Primary active role" : "Authorized role"}
                            >
                              {r}
                            </span>
                          ))}
                          {user.has_instructor_profile && (
                            <span className="cht-text-[9px] cht-font-black cht-px-2 cht-py-0.5 cht-rounded-md cht-bg-indigo-50 cht-text-[#3e57da]">
                              Faculty #{user.instructor_profile?.id || "Linked"}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Account Status */}
                      <td className="cht-px-6 cht-py-4">
                        <button
                          type="button"
                          onClick={() => handleToggleStatus(user)}
                          className="cht-inline-flex cht-items-center cht-gap-2 cht-cursor-pointer group/status"
                          title="Click to toggle status"
                        >
                          <div
                            className={`cht-w-2.5 cht-h-2.5 cht-rounded-full ${user.is_active ? "cht-bg-emerald-500" : "cht-bg-rose-500"
                              }`}
                          />
                          <span
                            className={`cht-text-xs cht-font-bold ${user.is_active ? "cht-text-slate-700" : "cht-text-rose-600"
                              } group-hover/status:cht-underline`}
                          >
                            {user.is_active ? "Active" : "Suspended"}
                          </span>
                        </button>
                      </td>

                      {/* Date Joined */}
                      <td className="cht-px-6 cht-py-4">
                        <p className="cht-text-xs cht-font-medium cht-text-slate-500">
                          {user.date_joined ? new Date(user.date_joined).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—"}
                        </p>
                      </td>

                      {/* Actions */}
                      <td className={`cht-px-6 cht-py-4 cht-text-right ${isMenuOpen ? "cht-relative cht-z-30" : ""}`}>
                        <div
                          ref={isMenuOpen ? dropdownRef : undefined}
                          className="cht-relative cht-inline-block"
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(isMenuOpen ? null : user.id);
                            }}
                            className="cht-w-9 cht-h-9 cht-flex cht-items-center cht-justify-center cht-rounded-xl cht-text-slate-400 hover:cht-bg-slate-100 hover:cht-text-slate-800 cht-transition-colors cht-cursor-pointer"
                            title="Actions"
                          >
                            <MoreVertical size={18} />
                          </button>

                          <AnimatePresence>
                            {isMenuOpen && (
                              <motion.div
                                initial={{ opacity: 0, y: isNearBottom ? -6 : 6, scale: 0.96 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: isNearBottom ? -6 : 6, scale: 0.96 }}
                                transition={{ duration: 0.15 }}
                                className={`cht-absolute cht-right-0 ${isNearBottom ? "cht-bottom-full cht-mb-2" : "cht-top-full cht-mt-2"
                                  } cht-w-56 cht-bg-white cht-rounded-2xl cht-shadow-2xl cht-border cht-border-gray-100 cht-p-1.5 cht-z-50`}
                              >
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveDropdown(null);
                                    setRoleModalUser(user);
                                  }}
                                  className="cht-w-full cht-flex cht-items-center cht-gap-2.5 cht-px-3 cht-py-2.5 cht-text-xs cht-font-bold cht-text-slate-700 hover:cht-bg-indigo-50/70 hover:cht-text-[#3e57da] active:cht-bg-indigo-100/60 cht-rounded-xl cht-transition-colors text-left cht-cursor-pointer"
                                >
                                  <Key size={14} className="cht-text-[#3e57da]" /> Assign & Manage Roles
                                </button>

                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveDropdown(null);
                                    setPermissionModalUser(user);
                                  }}
                                  className="cht-w-full cht-flex cht-items-center cht-gap-2.5 cht-px-3 cht-py-2.5 cht-text-xs cht-font-bold cht-text-slate-700 hover:cht-bg-purple-50/70 hover:cht-text-purple-600 active:cht-purple-100/60 cht-rounded-xl cht-transition-colors text-left cht-cursor-pointer"
                                >
                                  <Shield size={14} className="cht-text-purple-600" /> Manage Permissions
                                </button>

                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveDropdown(null);
                                    handleToggleStatus(user);
                                  }}
                                  className="cht-w-full cht-flex cht-items-center cht-gap-2.5 cht-px-3 cht-py-2.5 cht-text-xs cht-font-bold cht-text-slate-700 hover:cht-bg-slate-100/80 active:cht-bg-slate-200/60 cht-rounded-xl cht-transition-colors text-left cht-cursor-pointer"
                                >
                                  <Ban size={14} className={user.is_active ? "cht-text-amber-600" : "cht-text-emerald-600"} />
                                  {user.is_active ? "Suspend Account" : "Activate Account"}
                                </button>

                                <div className="cht-h-px cht-bg-gray-100 cht-my-1" />

                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveDropdown(null);
                                    setDeletingUser(user);
                                  }}
                                  className="cht-w-full cht-flex cht-items-center cht-gap-2.5 cht-px-3 cht-py-2.5 cht-text-xs cht-font-bold cht-text-rose-600 hover:cht-bg-rose-50 active:cht-bg-rose-100/80 cht-rounded-xl cht-transition-colors text-left cht-cursor-pointer"
                                >
                                  <Trash2 size={14} /> Delete Account
                                </button>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      {/* Role Assignment Modal */}
      <RoleAssignmentModal
        isOpen={Boolean(roleModalUser)}
        onClose={() => setRoleModalUser(null)}
        user={roleModalUser}
        onSuccess={() => {
          refetchUsers();
        }}
      />

      {/* Permissions Modal */}
      <UserPermissionModal
        isOpen={Boolean(permissionModalUser)}
        onClose={() => setPermissionModalUser(null)}
        user={permissionModalUser}
        onSuccess={() => {
          refetchUsers();
        }}
      />

      {/* Provision User Account Modal */}
      <ProvisionUserModal
        isOpen={isProvisionOpen}
        onClose={() => setIsProvisionOpen(false)}
        onSuccess={() => {
          refetchUsers();
        }}
      />

      {/* Delete User Confirmation Modal */}
      {deletingUser && typeof document !== "undefined" && createPortal(
        <AnimatePresence>
          <div
            className="cht-fixed cht-inset-0 cht-w-screen cht-h-screen cht-z-[99999] cht-flex cht-items-center cht-justify-center cht-p-4 sm:cht-p-6 cht-bg-slate-950/70 cht-backdrop-blur-sm cht-overflow-y-auto"
            role="dialog"
            aria-modal="true"
            onClick={() => !isDeleting && setDeletingUser(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="cht-bg-white cht-rounded-3xl cht-p-6 sm:cht-p-8 cht-max-w-md cht-w-full cht-shadow-2xl cht-border cht-border-gray-100 cht-space-y-4 cht-my-auto"
            >
              <div className="cht-w-12 cht-h-12 cht-rounded-2xl cht-bg-rose-50 cht-text-rose-600 cht-flex cht-items-center cht-justify-center">
                <AlertTriangle size={24} />
              </div>
              <div>
                <h3 className="cht-text-lg cht-font-bold cht-text-slate-900 !cht-font-heading">
                  Delete User Account?
                </h3>
                <p className="cht-text-xs cht-text-slate-500 cht-mt-1 leading-relaxed">
                  Are you sure you want to permanently remove <strong className="cht-text-slate-800">{deletingUser.email}</strong>? All associated progress and access records will be removed.
                </p>
              </div>
              <div className="cht-flex cht-items-center cht-justify-end cht-gap-3 cht-pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="cht-rounded-xl"
                  onClick={() => setDeletingUser(null)}
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  className="cht-rounded-xl !cht-bg-rose-600 hover:!cht-bg-rose-700"
                  onClick={handleDeleteUser}
                  isLoading={isDeleting}
                >
                  Confirm Delete
                </Button>
              </div>
            </motion.div>
          </div>
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default SuperAdminUsers;
